import React, { useEffect, useState } from 'react';
import { ClipboardList, FilePlus2, GitBranch, Inbox, ListChecks, Workflow } from 'lucide-react';
import * as api from '../../api';
import { RNLLoader } from '../UI';
import { StatTile } from './MTOShared';
import { NAVY } from './mtoUtils';

const SECTIONS = [
  { tab: 'new', Icon: FilePlus2, title: 'New Take-Off', text: 'Project → building/zone → discipline → system; the BOQ loads as the working area.' },
  { tab: 'register', Icon: ClipboardList, title: 'Take-Off Register', text: 'Every take-off across all projects with full traceability.' },
  { tab: 'my-takeoffs', Icon: ListChecks, title: 'My Take-Offs', text: 'Drafts, returned and submitted take-offs you prepared.' },
  { tab: 'approvals', Icon: Inbox, title: 'Approval Queue', text: 'Take-offs waiting at your approval step.' },
  { tab: 'revisions', Icon: GitBranch, title: 'Revision History', text: 'Never overwrite — R0 → R1 → R2, the approved revision supersedes the last.' },
];

const MAP = ['Project', 'Building / Zone', 'Discipline', 'Take-Off', 'Take-Off Approval', 'Material Request',
  'Cost Control', 'Store', 'Management Approval', 'Procurement', 'PO', 'GRN', 'Site Issue'];

export default function MTODashboard({ goTo }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.getTakeoffSummary().then(setSummary).catch(() => setSummary({ total: 0, by_status: {}, total_materials: 0 }));
  }, []);

  const s = summary?.by_status || {};
  const tiles = [
    { label: 'Total Take-Offs', value: summary?.total, accent: NAVY, tab: 'register' },
    { label: 'Draft', value: s.draft, accent: '#475569', tab: 'register', status: 'draft' },
    { label: 'Pending Approval', value: s.pending_approval, accent: '#ca8a04', tab: 'register', status: 'pending_approval' },
    { label: 'Returned', value: s.returned, accent: '#ea580c', tab: 'register', status: 'returned' },
    { label: 'Approved', value: s.approved, accent: '#16a34a', tab: 'register', status: 'approved' },
    { label: 'Total Materials', value: summary?.total_materials, accent: '#7c3aed' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: 'var(--text-primary)' }}>Material Take-Off &amp; Planning</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Engineering take-off → material request → cost control → store → procurement — one continuous material line.
          </p>
        </div>
        <button onClick={() => goTo('new')} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + New Material Take-Off
        </button>
      </div>

      {!summary ? <RNLLoader title="Loading dashboard..." subtitle="Counting take-offs" /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 22 }}>
            {tiles.map(t => (
              <StatTile key={t.label} {...t} onClick={t.tab ? () => goTo(t.tab, t.status ? { status: t.status } : undefined) : undefined} />
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 22 }}>
            {SECTIONS.map(({ tab, Icon, title, text }) => (
              <button key={tab} onClick={() => goTo(tab)} style={{
                textAlign: 'left', background: 'var(--bg-card)', border: '1px solid black', borderRadius: 12, padding: 16, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--text-primary)', fontFamily: 'var(--font)',
              }}>
                <span style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(11,49,139,0.1)', color: NAVY, display: 'grid', placeItems: 'center' }}><Icon size={19} /></span>
                <span style={{ fontWeight: 800, fontSize: 14 }}>{title}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{text}</span>
              </button>
            ))}
          </div>

          <div style={{ border: '1px solid black', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13, marginBottom: 10 }}>
              <Workflow size={16} /> Workflow Map
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
              {MAP.map((step, i) => (
                <React.Fragment key={step}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 11px', borderRadius: 8, background: i < 5 ? 'rgba(11,49,139,0.1)' : 'var(--bg-base)', color: i < 5 ? NAVY : 'var(--text-secondary)', border: '1px solid var(--border-light)' }}>{step}</span>
                  {i < MAP.length - 1 && <span style={{ color: '#94a3b8' }}>→</span>}
                </React.Fragment>
              ))}
            </div>
            <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Approvals run through the approval engine (Workflow Config → <b>MATERIAL_TAKEOFF</b>). Material Requests are raised from approved take-offs and keep the link back to them.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
